<template>
  <Teleport to="body">
    <div
      ref="toastEl"
      class="reward-toast"
      :class="`reward-toast--${position}`"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <div class="reward-toast__bar" />
      <div class="reward-toast__body">
        <div ref="coinWrap" class="reward-toast__coin-wrap">
          <img :src="resolvedCoin" alt="" class="reward-toast__coin" />
        </div>
        <div class="reward-toast__copy">
          <div class="reward-toast__eyebrow">{{ eyebrow }}</div>
          <div class="reward-toast__title">{{ title }}</div>
        </div>
        <div ref="pointsEl" class="reward-toast__points">+0</div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import gsap from 'gsap'
import defaultCoin from '@/../public/img/game/bolt.svg'

type Position = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'

const props = withDefaults(defineProps<{
  points: number
  coinSrc?: string
  eyebrow?: string
  title?: string
  position?: Position
  visibleFor?: number
  trigger?: string | number
  autoplay?: boolean
}>(), {
  coinSrc: '',
  eyebrow: 'Atividade concluída',
  title: 'Pontos adicionados',
  position: 'top-right',
  visibleFor: 1.5,
  trigger: 0,
  autoplay: false,
})

const emit = defineEmits<{ start: []; complete: [] }>()
const toastEl = ref<HTMLElement | null>(null)
const coinWrap = ref<HTMLElement | null>(null)
const pointsEl = ref<HTMLElement | null>(null)
const resolvedCoin = computed(() => props.coinSrc || defaultCoin)
let timeline: gsap.core.Timeline | null = null

async function play() {
  await nextTick()
  if (!toastEl.value || !coinWrap.value || !pointsEl.value) return

  timeline?.kill()
  gsap.killTweensOf([toastEl.value, coinWrap.value, pointsEl.value])
  emit('start')

  const counter = { value: 0 }
  const fromX = props.position.endsWith('right') ? 80 : -80
  gsap.set(toastEl.value, { opacity: 0, x: fromX, scale: 0.96 })
  gsap.set(coinWrap.value, { rotation: -45, scale: 0.4 })
  gsap.set(pointsEl.value, { scale: 0.7 })
  pointsEl.value.textContent = '+0'

  timeline = gsap.timeline({ onComplete: () => emit('complete') })
  timeline
    .to(toastEl.value, { opacity: 1, x: 0, scale: 1, duration: 0.42, ease: 'power3.out' }, 0)
    .to(coinWrap.value, { rotation: 0, scale: 1, duration: 0.45, ease: 'back.out(2.5)' }, 0.08)
    .to(pointsEl.value, { scale: 1.12, duration: 0.22, ease: 'back.out(2)' }, 0.12)
    .to(counter, {
      value: props.points,
      duration: 0.55,
      ease: 'power2.out',
      onUpdate: () => {
        if (pointsEl.value) pointsEl.value.textContent = `+${Math.round(counter.value).toLocaleString('pt-BR')}`
      },
    }, 0.12)
    .to(pointsEl.value, { scale: 1, duration: 0.16 }, 0.34)
    .to(toastEl.value, {
      opacity: 0,
      x: fromX * 0.3,
      duration: 0.3,
      ease: 'power2.in',
    }, 0.52 + Math.max(0, props.visibleFor))
}

defineExpose({ play })
watch(() => props.trigger, (value, oldValue) => { if (value !== oldValue) void play() })
onMounted(() => { if (props.autoplay) void play() })
onBeforeUnmount(() => timeline?.kill())
</script>

<style scoped>
.reward-toast {
  position: fixed;
  z-index: 2147483000;
  width: 330px;
  max-width: calc(100vw - 2rem);
  opacity: 0;
  pointer-events: none;
  overflow: hidden;
  border-radius: 1rem;
  background: #2e1065;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, .1);
  box-shadow: 0 16px 50px rgba(49, 28, 74, .18);
  will-change: transform, opacity;
}
.reward-toast--top-right { top: 1.25rem; right: 1.25rem; }
.reward-toast--top-left { top: 1.25rem; left: 1.25rem; }
.reward-toast--bottom-right { bottom: 1.25rem; right: 1.25rem; }
.reward-toast--bottom-left { bottom: 1.25rem; left: 1.25rem; }
.reward-toast__bar { height: .25rem; background: linear-gradient(90deg, #e879f9, #d8b4fe, #a78bfa); }
.reward-toast__body { padding: 1rem; display: flex; gap: .75rem; align-items: center; }
.reward-toast__coin-wrap {
  width: 3rem;
  height: 3rem;
  flex: 0 0 3rem;
  border-radius: 1rem;
  background: rgba(255, 255, 255, .1);
  display: flex;
  align-items: center;
  justify-content: center;
  will-change: transform;
}
.reward-toast__coin { display: block; width: 2rem; height: 2rem; max-width: none; }
.reward-toast__copy { min-width: 0; flex: 1; }
.reward-toast__eyebrow { font-size: .625rem; line-height: 1rem; text-transform: uppercase; letter-spacing: .18em; color: #f5d0fe; font-weight: 700; }
.reward-toast__title { font-weight: 800; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.reward-toast__points { flex: 0 0 auto; font-size: 1.5rem; line-height: 2rem; font-weight: 900; font-variant-numeric: tabular-nums; white-space: nowrap; }
</style>
